import { useState, type ClipboardEvent, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type { AdminQuestionnaireTemplateInput } from '../api';
import { applyJsonToTemplateValues, isRecord, templateValuesToJson } from '../clipboard';
import { QuestionnaireEmailFields } from './QuestionnaireEmailFields';

interface DraftState {
  label: string;
  templateKey: string;
  active: boolean;
  emailSubject: string;
  emailBodyHtml: string;
  emailBodyText: string;
  emailServerId: string;
}

const EMPTY_DRAFT: DraftState = {
  label: '',
  templateKey: '',
  active: true,
  emailSubject: '',
  emailBodyHtml: '',
  emailBodyText: '',
  emailServerId: '',
};

interface FieldErrors {
  label?: string;
  templateKey?: string;
}

type ClipboardNote = { readonly kind: 'ok' | 'error'; readonly text: string } | null;

export function CreateQuestionnaireTemplateDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: AdminQuestionnaireTemplateInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [copied, setCopied] = useState(false);
  const [clipboardNote, setClipboardNote] = useState<ClipboardNote>(null);

  function handleClose(next: boolean): void {
    if (!next) {
      setDraft(EMPTY_DRAFT);
      setErrors({});
      setCopied(false);
      setClipboardNote(null);
    }
    onOpenChange(next);
  }

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(templateValuesToJson(draft), null, 2));
      setCopied(true);
      setClipboardNote(null);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setClipboardNote({ kind: 'error', text: t('routes.admin.questionnaires.templates.form.clipboard.copyError') });
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const text = event.clipboardData.getData('text');
    if (text.trim().length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;
    const { values, applied } = applyJsonToTemplateValues(draft, parsed);
    if (applied === 0) return;
    event.preventDefault();
    setDraft(values);
    setErrors({});
    setClipboardNote({
      kind: 'ok',
      text: t('routes.admin.questionnaires.templates.form.clipboard.imported', { count: applied }),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const label = draft.label.trim();
    const templateKey = draft.templateKey.trim();
    const next: FieldErrors = {};
    if (label.length === 0) next.label = t('routes.admin.questionnaires.templates.form.errors.labelRequired');
    else if (label.length > 255) next.label = t('routes.admin.questionnaires.templates.form.errors.labelTooLong');
    if (templateKey.length === 0)
      next.templateKey = t('routes.admin.questionnaires.templates.form.errors.templateKeyRequired');
    else if (templateKey.length > 255)
      next.templateKey = t('routes.admin.questionnaires.templates.form.errors.templateKeyTooLong');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      label,
      templateKey,
      active: draft.active,
      emailSubject: draft.emailSubject,
      emailBodyHtml: draft.emailBodyHtml,
      emailBodyText: draft.emailBodyText,
      emailServerId: draft.emailServerId,
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[54rem]" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.questionnaires.templates.create.title')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          noValidate
          className="space-y-4 max-h-[75vh] overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              id="questionnaire-template-label"
              label={t('routes.admin.questionnaires.templates.form.label')}
              required
              error={errors.label}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.label}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, label: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
            <FormField
              id="questionnaire-template-key"
              label={t('routes.admin.questionnaires.templates.form.templateKey')}
              required
              error={errors.templateKey}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.templateKey}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, templateKey: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="ppq"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <label className="inline-flex items-center gap-2 text-sm sm:pt-7">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, active: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.questionnaires.templates.form.active')}
            </label>
          </div>

          <QuestionnaireEmailFields
            idPrefix="qt-create"
            values={{
              emailSubject: draft.emailSubject,
              emailBodyHtml: draft.emailBodyHtml,
              emailBodyText: draft.emailBodyText,
              emailServerId: draft.emailServerId,
            }}
            onChange={(field, value) => {
              setDraft((p) => ({ ...p, [field]: value }));
            }}
          />
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.questionnaires.templates.form.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter className="sm:items-center sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied
                  ? t('routes.admin.questionnaires.templates.form.clipboard.copied')
                  : t('routes.admin.questionnaires.templates.form.clipboard.copy')}
              </Button>
              {clipboardNote !== null ? (
                <span
                  role={clipboardNote.kind === 'error' ? 'alert' : undefined}
                  className={
                    clipboardNote.kind === 'error' ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
                  }
                >
                  {clipboardNote.text}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t('routes.admin.questionnaires.templates.form.clipboard.pasteHint')}
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleClose(false);
                }}
                disabled={pending}
              >
                {t('routes.admin.questionnaires.templates.form.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {t('routes.admin.questionnaires.templates.create.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
